import {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  type FocusEvent,
} from 'react';

import debounce from 'lodash/debounce';
import {
  FieldProps,
  FormSpy,
  useField,
  useForm,
  FieldRenderProps,
} from 'react-final-form';
import Collapse from '@mui/material/Collapse';

import { TextInput as Input } from '@vk-hr-tek/ui/input';

import { Autorecognition } from './Autorecognition';

type ValidateType = (
  value: string | undefined,
  allValues: object,
) => (string | undefined) | Promise<string | undefined>;

/**
 * Компонент TextInput.
 *
 * @public
 * @param label - Метка поля.
 * @param labelIcon - Иконка для метки.
 * @param name - Имя поля.
 * @param onChange - Колбэк на изменение значения.
 * @param type - Тип поля ввода.
 * @param mask - Маска ввода.
 * @param placeholder - Подсказка для поля ввода.
 * @param multiline - Флаг для многострочного поля.
 * @param rows - Количество строк для многострочного поля.
 * @param maxRows - Максимальное количество строк.
 * @param showError - Флаг для отображения ошибки.
 * @param required - Флаг для обязательного поля.
 * @param tooltip - Всплывающая подсказка.
 * @param after - Функция для отображения дополнительного контента после поля.
 * @param disabled - Флаг для отключения поля.
 * @param loading - Флаг для отображения состояния загрузки.
 * @param clearable - Флаг для очистки поля.
 * @param recognizedValue - Распознанное значение.
 * @param isRecognitionUsedBefore - Флаг использования распознавания ранее.
 * @param validationWithoutTouch - Флаг валидации без взаимодействия с полем.
 * @param validationWhenDisabled - Флаг валидации когда поле отключено.
 * @param inputMode - Режим ввода.
 * @param inputFilter - Фильтрации значения поля ввода.
 * @param autocomplete - Атрибут автозаполнения.
 * @param testId - Идентификатор для тестирования.
 * @param validateArray - Массив валидаторов.
 * @param warnings - Массив валидаторов для предупреждений.
 * @param maxLength - Максимальная длина вводимого текста.
 * @param minLength - Минимальная длина вводимого текста.
 * @param helperText - Вспомогательный текст под полем.
 * @param debounceTimeout - Таймаут дебаунса для обработчика изменения.
 * @param rest - Остальные пропсы, передаваемые в {@link useField}.
 * @returns Рендеринг поля ввода с автозаполнением.
 */
const TextInput = ({
  label,
  labelIcon,
  name,
  onChange,
  type,
  mask,
  placeholder,
  multiline = false,
  rows = 1,
  maxRows,
  showError = true,
  required = false,
  tooltip,
  after,
  disabled = false,
  loading = false,
  clearable = false,
  recognizedValue,
  isRecognitionUsedBefore = false,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
  inputMode,
  inputFilter,
  autocomplete,
  testId,
  validateArray,
  warnings,
  maxLength,
  minLength,
  helperText,
  debounceTimeout,
  ...rest
}: {
  after?: (value: string | undefined) => ReactNode;
  debounceTimeout?: number;
  inputFilter?: (value: string | undefined) => string | undefined;
} & FieldProps<string | undefined, FieldRenderProps<string | undefined>> & {
    validateArray?: (ValidateType | undefined)[];
    warnings?: ValidateType[] | undefined;
  }) => {
  const { mutators } = useForm();

  /**
   * Композитный валидатор.
   *
   * @param validators - Массив валидаторов.
   * @returns Валидатор, объединяющий результаты всех переданных валидаторов.
   */
  const composeValidators =
    (validators: (ValidateType | undefined)[]) =>
    (value: string | undefined, allValues: object) => {
      const result = validators.reduce(
        (error: unknown, validator?: ValidateType) =>
          error || validator?.(value, allValues),
        undefined,
      );
      return result;
    };

  const composedWarnings = composeValidators(warnings || []);

  const { input, meta } = useField(name, {
    validate: composeValidators(validateArray || []),
    ...rest,
  });

  const { onBlur, onChange: onInputChange } = input;

  const [value, setValue] = useState(input.value);
  const lastSetByUser = useRef(input.value);

  // Синхронизация локального стейта при внешнем изменении формы
  if (debounceTimeout && input.value !== lastSetByUser.current) {
    setValue(input.value);
    lastSetByUser.current = input.value;
  }

  /**
   * Обработчик изменения значения поля.
   *
   * @param formValue - Новое значение поля.
   */
  const handleChangeForm = useCallback(
    (formValue?: string) => {
      lastSetByUser.current = formValue;
      onInputChange(formValue);

      if (onChange) {
        onChange(formValue);
      }
    },
    [onInputChange, onChange],
  );

  const handleChangeDebounce = useMemo(
    () => debounce(handleChangeForm, debounceTimeout),
    [debounceTimeout, handleChangeForm],
  );

  const handleChange = useCallback(
    (changedValue?: string) => {
      setValue(changedValue);

      if (debounceTimeout) {
        handleChangeDebounce(changedValue);

        if (changedValue === '') {
          handleChangeForm(changedValue);
        }
      } else {
        handleChangeForm(changedValue);
      }
    },
    [debounceTimeout, handleChangeDebounce, handleChangeForm],
  );

  const handleBlur = useCallback(
    (event?: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleChangeDebounce.flush();
      onBlur(event);
    },
    [handleChangeDebounce, onBlur],
  );

  useEffect(() => {
    return () => {
      handleChangeDebounce.flush();
    };
  }, [handleChangeDebounce]);

  /**
   * Флаг для отображения ошибки в зависимости от состояния disabled.
   */
  const errorDisabled = disabled || !validationWhenDisabled;

  const finalValue = debounceTimeout ? value : input.value;

  /**
   * Флаг для отображения ошибки.
   */
  const renderError = useMemo(
    () =>
      !errorDisabled && validationWithoutTouch
        ? !!input.value && !!meta.error
        : meta.touched && !!meta.error,
    [
      errorDisabled,
      input.value,
      meta.error,
      meta.touched,
      validationWithoutTouch,
    ],
  );

  /**
   * Флаг для отображения ворнинга.
   */
  const renderWarning = useMemo(
    () =>
      !errorDisabled && validationWithoutTouch
        ? !!input.value && !!meta.data?.warning
        : meta.touched && !!meta.data?.warning,
    [
      errorDisabled,
      input.value,
      meta.data?.warning,
      meta.touched,
      validationWithoutTouch,
    ],
  );

  return (
    <div>
      <Autorecognition
        recognizedValue={recognizedValue}
        isRecognitionUsedBefore={isRecognitionUsedBefore}
        onBlur={handleBlur}
        disabled={disabled}
        setValue={handleChange}
        value={finalValue}
        mask={mask}
      >
        <Input
          {...input}
          testId={testId}
          label={label}
          labelIcon={labelIcon}
          required={required}
          disabled={disabled}
          value={finalValue}
          inputFilter={inputFilter}
          onChange={handleChange}
          onBlur={handleBlur}
          type={type}
          error={renderError}
          helperText={(renderError && showError && meta.error) || helperText}
          warning={renderWarning && meta.data?.warning}
          placeholder={placeholder}
          tooltip={tooltip}
          multiline={multiline}
          rows={rows}
          maxRows={maxRows}
          mask={mask}
          loading={loading}
          clearable={clearable}
          inputMode={inputMode}
          autocomplete={autocomplete}
          maxLength={maxLength}
          minLength={minLength}
        />

        <FormSpy
          subscription={{ values: true }}
          onChange={({ values }) => {
            if (values) {
              mutators.setFieldData(input.name, {
                warning: composedWarnings(values[input.name], values),
              });
            }
          }}
        />

        {after && typeof after === 'function' && (
          <Collapse in={meta.visited}>{after(input.value)}</Collapse>
        )}
      </Autorecognition>
    </div>
  );
};

export default TextInput;
